<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.gamification_sets', 2),
                        href: meta.links.set_index,
                    },
                    {
                        name: gamificationSet.breadcrumb_title,
                        href: meta.links.set_show,
                    },
                    {
                        name: $tc('messages.itineraries', 2),
                        href: meta.links.index,
                    },
                    {
                        name: itinerary.name,
                        href: null,
                    },
                    {
                        name: $t('messages.edit'),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.update" method="post" @submit.prevent="submit">
            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.information') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.name')" for="name" />
                            <FormInput
                                required
                                id="name"
                                v-model="form.name"
                                name="name"
                                type="text"
                                max="180"
                                class="w-full mt-1"
                            />
                            <FormError :value="form.errors.name" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.description')" for="description" />
                            <FormTextarea
                                rows="3"
                                id="description"
                                v-model="form.description"
                                name="description"
                                type="text"
                                class="mt-1 w-full"
                                style="min-height: 60px"
                            />
                            <FormError :value="form.errors.description" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6 hidden">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.type')" for="type" />
                            <FormInput
                                required
                                id="type"
                                v-model="form.type"
                                name="type"
                                type="text"
                                max="191"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.type" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-3">
                        <div class="mt-1">
                            <FormLabel :value="$tc('messages.groups', 2)" for="group_ids" />
                            <SlideOverCheckboxInput v-model="form.group_ids" :options="options.groups" />
                            <FormError :value="form.errors.group_ids" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.date') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.published_at')" for="published_at" />
                            <FormInput
                                id="published_at"
                                v-model="form.published_at"
                                name="published_at"
                                type="datetime-local"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.published_at" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.unpublished_at')" for="unpublished_at" />
                            <FormInput
                                id="unpublished_at"
                                v-model="form.unpublished_at"
                                name="unpublished_at"
                                type="datetime-local"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.unpublished_at" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.work_schedule')">
                                <span class="text-gray-500">{{ $t('messages.(optional)') }}</span>
                            </FormLabel>
                            <div class="flex gap-2">
                                <div>
                                    <FormInput
                                        id="time_start"
                                        v-model="form.time_start"
                                        name="time_start"
                                        type="time"
                                        class="mt-1"
                                    />
                                    <FormError :value="form.errors.time_start" class="mt-2" />
                                </div>
                                <div>
                                    <FormInput
                                        id="time_end"
                                        v-model="form.time_end"
                                        name="time_end"
                                        type="time"
                                        class="mt-1"
                                    />
                                    <FormError :value="form.errors.time_end" class="mt-2" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $tc('messages.images', 1) }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.itinerary_poster')" for="image">
                                <span class="text-gray-400 text-xs">(500&times;350)</span>
                            </FormLabel>
                            <input
                                id="image"
                                @input="form.image = $event.target.files[0]"
                                name="image"
                                type="file"
                                accept="image/png, image/jpeg"
                                class="mt-1 w-full"
                            />
                            <FileInputInfo :accepts="['png', 'jpg']" />
                            <FormError :value="form.errors.image" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    <div>
                        {{ $tc('messages.itinerary_stations', 2) }}
                    </div>
                    <div class="w-40 h-40" style="transform: rotateX(45deg); transform-style: preserve-3d">
                        <StationBase :color="form.base_color" :shape="form.base_shape" with-shadow>
                            <svg
                                class="w-1/2 h-1/2 text-white opacity-50"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                                ></path>
                            </svg>
                        </StationBase>
                    </div>
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.station_shape')" for="base_shape" />
                            <div class="flex space-x-2">
                                <div v-for="shape in ['pentagon', 'circle']" :key="shape">
                                    <div
                                        class="w-10 h-10 transition ease-in-out delay-150"
                                        :class="{ 'opacity-50 w-10 h-10': form.base_shape !== shape }"
                                        @click="form.base_shape = shape"
                                    >
                                        <StationBase :color="form.base_color" :shape="shape" />
                                    </div>
                                </div>
                            </div>
                            <FormError :value="form.errors.base_shape" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$tc('messages.colours', 1)" for="base_color" />
                            <FormNamedColorPicker v-model="form.base_color" />
                            <FormError :value="form.errors.base_color" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <div class="flex justify-end mt-5">
                <AppButton desired-action element="button" type="submit" :disabled="form.processing">
                    {{ $t('messages.save') }}
                    <span v-if="form.processing" class="ml-1 btn-spinner"></span>
                </AppButton>
            </div>
        </form>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import AppButton from '@/components/AppButton.vue';
import FormTextarea from '@/components/Forms/FormTextarea.vue';
import FormNamedColorPicker from '@/components/Forms/FormNamedColorPicker.vue';
import StationBase from '@/components/Forms/StationBase.vue';
import SlideOverCheckboxInput from '@/components/Forms/SlideOverCheckboxInput.vue';
import FileInputInfo from '@/components/FileInputInfo.vue';

export default {
    components: {
        FileInputInfo,
        SlideOverCheckboxInput,
        PanelPage,
        TitleFieldsFormSection,
        FormInput,
        FormLabel,
        FormError,
        AppButton,
        FormTextarea,
        StationBase,
        FormNamedColorPicker,
    },
    layout: Layout,
    name: 'Edit',
    props: ['meta', 'gamificationSet', 'options', 'itinerary'],
    data() {
        return {
            form: this.$inertia.form({
                _method: 'patch',
                name: this.itinerary.name,
                description: this.itinerary.description,
                type: this.itinerary.type,
                published_at: this.itinerary.published_at,
                unpublished_at: this.itinerary.unpublished_at,
                time_start: this.itinerary.time_start,
                time_end: this.itinerary.time_end,
                base_shape: this.itinerary.base_shape,
                base_color: this.itinerary.base_color,
                background_color: this.itinerary.background_color,
                stage_id: this.itinerary.stage_id,
                group_ids: this.itinerary.group_ids,
                image: null,
            }),
        };
    },
    methods: {
        submit() {
            this.form.post(this.meta.links.update);
        },
    },
};
</script>
